<template>
  <div class="listing" v-if="queryData">
    <MetaTags v-if="metaInfo" :meta-info="metaInfo" />
    <!-- Hero -->
    <section v-if="queryData?.assets" class="listing__hero">
      <listing-hero :listing-images="listingImages"></listing-hero>
    </section>

    <sticky-navigation :anchor-links="anchorLinks" :contact-links="contactLinks">
      <template v-if="queryData?.agency">
        <agent-contact :agency="queryData?.agency"></agent-contact>
      </template>
    </sticky-navigation>

    <div class="stripe basics">
      <div v-if="queryData" class="stripe__inner-container">
        <listing-basics :listing-data="queryData" :scrollToId="contactFormId"></listing-basics>
      </div>
    </div>

    <div v-if="queryData">
      <div class="stripe">
        <div class="stripe__inner-container">
          <listing-data-sheet :listing-data="queryData" :scrollToId="listingDataSheetId"></listing-data-sheet>
        </div>
      </div>
    </div>

    <div class="stripe">
      <div id="promos" class="stripe__inner-container">
        <listing-promos :baseInfo="queryData?.baseInfo" :featureGroups="featureGroups"></listing-promos>
      </div>
    </div>

    <div class="stripe blueprint">
      <div v-if="hasBlueprintPicture" class="listing-blueprint">
        <div class="stripe__inner-container blueprint-container">
          <div class="blueprint-wrapper">
            <h2 :id="listingBlueprintId">Pohja</h2>
            <img class="listing-image blueprint" :src="bluePrintPicture.url" alt="Kohteen pohjakuva" />
          </div>
        </div>
      </div>
    </div>
    <div id="listing-demographics" class="stripe" v-if="demographics">
      <div class="stripe__inner-container">
        <demographics :demographics="demographics"></demographics>
      </div>
    </div>

    <div class="stripe images" v-if="hasImages">
      <div id="images">
        <div class="stripe__inner-container image-list">
          <listing-images :images="listingImages" :scrollToId="listingImagesId"></listing-images>
        </div>
      </div>
    </div>

    <div id="listing-map" class="stripe map" v-if="listingHasCoordinates">
      <listing-map :base-info="queryData?.baseInfo" :scrollToId="listingMapId"></listing-map>
    </div>

    <div v-if="queryData" class="stripe">
      <div class="stripe__inner-container">
        <listing-agency :agency="queryData?.agency" :listing="queryData"></listing-agency>
      </div>
    </div>

    <div id="suggestions" class="stripe" v-if="listings?.length > 0">
      <div class="stripe__inner-container suggestions">
        <suggested-listings title="Kohteita samalla alueella" :suggestions="listings"></suggested-listings>
      </div>
    </div>

    <floating-finance-ad
      v-if="selectedFloatingAd"
      :title="selectedFloatingAd.title"
      :link="selectedFloatingAd.link"
    ></floating-finance-ad>

    <spinner :use-custom-property="true" :loading-property="isLoading"></spinner>

    <component :is="'script'" type="application/ld+json">
      {{ getSeoJSON }}
    </component>
  </div>
  <loading v-else-if="isPending"></loading>
  <error-message
    v-else-if="isError"
    class="error"
    :message="errorMsg"
    :button-text="errorBtnText"
    :retry="false"
  ></error-message>
</template>

<script lang="ts">
import MetaTags from '@/app/components/MetaTags.vue';
import * as actions from '@/app/store/action-types';
import config from '@/config';
import utils from '@/app/utils/utils';
import { listing as listingRoute } from '@/app/routes';
import Spinner from '@/app/components/spinner/spinner.vue';
import ErrorMessage from '@/app/components/error-message/error-message.vue';
import Loading from '@/app/components/loading/loading.vue';
import { computed, defineComponent, defineAsyncComponent } from 'vue';
import useListingData from '@/app/data/useListingData';
import listingUtils from '@/app/utils/listing-utils';
import featureGroups from '@/config/featureGroups';
import useDemographicsData from '@/app/data/useDemographicsData';
import { useQueryClient } from '@tanstack/vue-query';
import { useListingsData } from '@/app/data/useListingsData';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import isSSR from '@/utils/isSSR';
import logger from '@/app/logger';
import { isNotFoundError } from '@/utils/errors';

export default defineComponent({
  name: 'listing',
  setup() {
    const queryClient = useQueryClient();
    const route = useSafeRoute();
    const errorMsg = computed(() =>
      isNotFoundError(error.value)
        ? 'Kohdetta ei löytynyt. Se on voinut poistua myynnistä tai kohdenumero on virheellinen.'
        : ''
    );
    const errorBtnText = isSSR ? '' : 'Palaa edelliselle sivulle';

    // Fetch the listing
    const routeId = computed(() => {
      return getRouteParamAsString(route.value.params.id) || '';
    });
    const { data: queryData, isLoading, isPending, isError, error } = useListingData(routeId);

    // Fetch suggested listings (on the client)
    const suggestedListingsEnabled = computed(
      () => !!queryData?.value && !!queryData?.value?.baseInfo?.location?.districtId
    );
    const listingFilter = computed(() => ({
      group: computed(() => queryData?.value?.baseInfo?.listingGroup),
      type: computed(() => queryData?.value?.baseInfo?.listingType),
      districtId: computed(() => queryData?.value?.baseInfo?.location?.districtId),
      limit: 4,
    }));
    const { data: listingsData } = useListingsData(listingFilter, suggestedListingsEnabled, routeId);
    const listings = computed(() => listingsData?.value?.listings);

    // Fetch demographics (on the client)
    const postalCode = computed(() => queryData?.value?.baseInfo?.location?.postalCode);
    const { data: demographics } = useDemographicsData(postalCode);

    return {
      isLoading,
      isPending,
      queryData,
      queryClient,
      listings,
      demographics,
      route,
      isError,
      errorMsg,
      errorBtnText,
    };
  },
  data: () => ({
    contactFormId: 'contact-form-title',
    listingDataSheetId: 'listing-data-sheet-title',
    listingBlueprintId: 'blueprint-title',
    listingImagesId: 'listing-images-title',
    listingMapId: 'listing-map-title',
    featureGroups: featureGroups,
    config,
    analyticsEventTriggered: false,
    floatingFinanceAds: {
      sale: {
        title: '<span>Hae asuntolainaa</span><span>Osuuspankista!</span>',
        link: 'https://www.op.fi/henkiloasiakkaat/lainat-ja-asunnot/asuntolaina?cmpid=.haarpa..hark.opkoti.banner.kohdekuvissa.ha_jn_asuntolaina',
      },
      rental_apartment_below_45: {
        title: '<span>Mitä maksaa</span><span>kotivakuutus?</span>',
        link: 'https://www.op.fi/henkiloasiakkaat/vakuutukset/kotivakuutus/',
      },
      rental_apartment_above_45: {
        title: '<span>Katso kotivakuutus</span><span>tälle kohteelle</span>',
        link: 'https://www.op.fi/henkiloasiakkaat/vakuutukset/kotivakuutus?cmpid=.havaku..huom.op-koti.textlink.kerros_ja_rivi.ha_jn_kotivakuutus',
      },
      rental_house: {
        title: '<span>Katso kotivakuutus</span><span>tälle kohteelle</span>',
        link: 'https://www.op.fi/henkiloasiakkaat/vakuutukset/kotivakuutus/omakotitalo?cmpid=.havaku..huom.op-koti.textlink.omakotitalo.ha_jn_kotivakuutus',
      },
      rental_other: {
        title: '<span>Katso kotivakuutus</span><span>uuteen kotiin</span>',
        link: 'https://www.op.fi/henkiloasiakkaat/vakuutukset/kotivakuutus/?cmpid=.havaku..huom.op-koti.textlink.yleinen.ha_jn_kotivakuutus',
      },
    },
  }),
  watch: {
    queryData: {
      handler(newVal) {
        if (!isSSR && !!newVal) {
          logger.info('Sending analytics for pageName:' + newVal);
          this.fireAnalyticsPageView();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    fireAnalyticsPageView() {
      const eVar59Value = `${this.isRentalListing ? 'rent' : 'sale'};${this.queryData?.baseInfo.listingType}`;

      void void this.$store.dispatch(actions.analytics.set, {
        pageName: `kohde:${this.$route.params.id}`,
        eVar55: this.$route.params.id,
        eVar17: this.metaInfo.title,
        eVar57:
          this.queryData.source === config.LISTING_SOURCES.NEWSEC
            ? 'newsec'
            : this.queryData.agency && this.queryData.agency.office
              ? this.queryData.agency.office.id || this.queryData.agency.company.id
              : '',
        eVar59: eVar59Value,
      });
      void void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
      void void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.propertyView);
    },
    getPageTitle() {
      if (!this.fullAddress) {
        return 'Kohdetta ei löytynyt - OP Koti';
      }
      const areaText = '';
      // enable this when primaryArea is available:
      // const areaText =
      //  this.primaryArea && this.primaryArea.size ? ` (${this.primaryArea.size} ${this.primaryArea.displayUnit})` : '';
      return `${this.fullAddress}${areaText} - OP Koti`;
    },
    getMetaDescription() {
      return (
        (this.queryData?.baseInfo &&
          this.queryData?.baseInfo.description &&
          this.queryData?.baseInfo.description.join(' ')) ||
        ''
      );
    },
  },
  computed: {
    fullAddress() {
      let fullAddress = '';
      const street = this.queryData?.baseInfo?.location?.streetAddress.trim();
      const district = this.queryData?.baseInfo?.location?.district;
      const city = this.queryData?.baseInfo?.location?.city;

      if (street) {
        fullAddress += street;
      }

      if (district) {
        fullAddress += street ? `, ${district}` : district;
      }

      if (city) {
        fullAddress += street || district ? `, ${city}` : city;
      }

      return fullAddress;
    },
    listingImages() {
      const pictures = this.queryData?.assets?.pictures;
      return pictures
        ? listingUtils.sortImages(pictures, listingUtils.getVirtualPresentation(this.queryData?.assets?.links))
        : [];
    },
    listingHasCoordinates() {
      const coordinates = this.queryData?.baseInfo?.location?.coordinates;
      return coordinates && coordinates.latitude && coordinates.longitude;
    },
    hasImages() {
      return this.listingImages?.length > 0;
    },
    bluePrintPicture() {
      return listingUtils.getBluePrintPicture(this.listingImages);
    },
    hasBlueprintPicture() {
      return !!this.bluePrintPicture;
    },
    anchorLinks() {
      const links = [
        {
          scrollToId: this.listingDataSheetId,
          iconClass: 'icon-info-circled',
          label: 'Tiedot',
        },
      ];

      if (this.hasBlueprintPicture) {
        links.push({
          scrollToId: this.listingBlueprintId,
          iconClass: 'icon-house_rooms-circled',
          label: 'Pohja',
        });
      }

      if (this.hasImages) {
        links.push({
          scrollToId: this.listingImagesId,
          iconClass: 'icon-camera-circled',
          label: 'Kuvat',
        });
      }

      if (this.listingHasCoordinates) {
        links.push({
          scrollToId: this.listingMapId,
          iconClass: 'icon-location-circled',
          label: 'Sijainti',
        });
      }

      return links;
    },
    contactLinks() {
      return [
        {
          scrollToId: this.contactFormId,
          iconClass: 'icon-speech-bubble-circled',
          label: 'Viesti',
        },
      ];
    },
    selectedFloatingAd() {
      // Rental
      if (this.isRentalListing && this.queryData) {
        if (this.queryData.baseInfo?.listingType === 90) {
          // Omakotitalo
          return this.floatingFinanceAds.rental_house;
        } else if (this.isApartmentOrRowhouse) {
          // Kerrostalo or Rivitalo
          if (this.queryData.housing?.livingArea.size <= 45) {
            return this.floatingFinanceAds.rental_apartment_below_45;
          } else {
            return this.floatingFinanceAds.rental_apartment_above_45;
          }
        } else {
          return this.floatingFinanceAds.rental_other;
        }
      } else {
        // Sale
        return this.floatingFinanceAds.sale;
      }
    },
    isRentalListing() {
      return this.queryData?.baseInfo?.featureGroups?.includes(this.featureGroups.RENTALS.id);
    },
    getSeoJSON() {
      return `{
      "@context":"http://schema.org",
      "@type":["Product","Apartment"],
      "name":"${this.queryData?.baseInfo?.location.streetAddress}, ${this.queryData?.baseInfo?.location.postalCode} ${
        this.queryData?.baseInfo?.location.city
      }",
      "description":"${this.queryData?.baseInfo?.description}",
      "url":"https://op-koti.fi${this.route.fullPath}"
      ${
        this.queryData?.housing && this.queryData?.housing.hasOwnProperty('numberOfRooms')
          ? `,"numberOfRooms": ${this.queryData?.housing.numberOfRooms},`
          : ','
      }
      "sku":${this.queryData?.id},
      "floorSize":
        {
          "@type":"QuantitativeValue",
          "unitCode":"MTK",
          "unitText":"m²"
          ${
            this.queryData?.housing &&
            this.queryData?.housing.hasOwnProperty('totalArea') &&
            this.queryData?.housing.totalArea.hasOwnProperty('size')
              ? `,"value": ${this.queryData?.housing.totalArea.size}`
              : ''
          }
        },
      "address":
        {
          "@type":"PostalAddress",
          "addressCountry":"Suomi",
          "addressRegion":"${this.queryData?.baseInfo?.location.region}",
          "postalCode":"${this.queryData?.baseInfo?.location.postalCode}",
          "streetAddress":"${this.queryData?.baseInfo?.location.streetAddress}",
          "addressLocality":"${this.queryData?.baseInfo?.location.district}",
          "image":"${this.mainImage}"
        },
      "geo":
        {
          "@type":"GeoCoordinates",
          "latitude":"${
            this.queryData?.baseInfo?.location.hasOwnProperty('coordinates')
              ? this.queryData?.baseInfo?.location.coordinates.latitude
              : ''
          }",
          "longitude":"${
            this.queryData?.baseInfo?.location.hasOwnProperty('coordinates')
              ? this.queryData?.baseInfo?.location.coordinates.longitude
              : ''
          }"
        },
      "offers":
        {
          "@type":"Offer",
          "businessFunction":"${this.getBusinessFunction}",
          "price":${this.queryData?.pricing.price},
          "priceCurrency":"EUR"
        }
      }`;
    },
    getBusinessFunction() {
      if (this.queryData?.baseInfo.modeOfHabitation === 'OM') return 'http://purl.org/goodrelations/v1#Sell';
      else if (this.queryData?.baseInfo.modeOfHabitation === 'VU') return 'http://purl.org/goodrelations/v1#LeaseOut';
      else return '';
    },
    mainImage() {
      if (this.queryData?.assets?.mainPicture) {
        return this.queryData?.assets.mainPicture;
      }
      if (this.queryData?.assets?.pictures) {
        const mainPicAsset = this.queryData?.assets?.pictures?.find((img: any) => img.subType === 'mainPicture');
        return mainPicAsset ? mainPicAsset.url : null;
      }
      return null;
    },
    metaInfo() {
      const id = getRouteParamAsString(this.route.params.id);
      const metaInfo = {
        title: this.getPageTitle(),
        meta: utils.getMetaTagsForPage(
          this.fullAddress,
          this.getMetaDescription(),
          this.queryData?.assets?.mainPicThumbnail || ''
        ),
        script: [],
        link: [
          {
            rel: 'canonical',
            href: `${config.FRONT.PROD_BASE_URL}${listingRoute.path}/${id}`,
          },
        ],
      };

      return metaInfo;
    },
    isApartmentOrRowhouse() {
      return this.queryData?.baseInfo?.listingType === 89 || this.queryData?.baseInfo?.listingType === 91;
    },
  },
  components: {
    MetaTags,
    AgentContact: defineAsyncComponent(() => import('./listing-agent-contact.vue')),
    StickyNavigation: defineAsyncComponent(() => import('@/app/components/sticky-navigation/sticky-navigation.vue')),
    ListingHero: defineAsyncComponent(() => import('./listing-hero.vue')),
    SuggestedListings: defineAsyncComponent(() => import('@/app/components/suggested-listings/suggested-listings.vue')),
    FloatingFinanceAd: defineAsyncComponent(
      () => import('@/app/components/floating-finance-ad/floating-finance-ad.vue')
    ),
    ListingBasics: defineAsyncComponent(() => import('./listing-basics.vue')),
    ListingDataSheet: defineAsyncComponent(() => import('./listing-data-sheet.vue')),
    ListingImages: defineAsyncComponent(() => import('./listing-images.vue')),
    ListingMap: defineAsyncComponent(() => import('./listing-map.vue')),
    ListingAgency: defineAsyncComponent(() => import('./listing-agency.vue')),
    ListingPromos: defineAsyncComponent(() => import('./listing-promos.vue')),
    Spinner,
    IframeContainer: defineAsyncComponent(() => import('@/app/components/iframe-container/iframe-container.vue')),
    Demographics: defineAsyncComponent(() => import('@/app/components/demographics/demographics.vue')),
    ErrorMessage,
    Loading,
  },
});
</script>
